import { Check } from 'lucide-react';
import { PlaceBadge } from '@/components/design';
import type { Match } from '@/data/types';

interface Props {
  match: Match;
  onClick?: () => void;
  /** Карта participantId → метка итогового места («3», «5-8»). */
  placeByParticipant?: Map<string, string>;
  /** Растянуть карточку на размер родителя (для абсолютной раскладки сетки). */
  fill?: boolean;
  /** Метка в шапке матча, заменяет «Раунд N · Матч M». */
  label?: string;
  /** Пометить как финал — акцентная подпись в шапке. */
  isFinal?: boolean;
}

export function MatchCard({ match, onClick, placeByParticipant, fill, label, isFinal }: Props) {
  const clickable = !!onClick && match.status !== 'PENDING';
  const isTBD = !match.playerAId || !match.playerBId;
  const winnerIsA = match.score?.winnerId === match.playerAId;
  const winnerIsB = match.score?.winnerId === match.playerBId;
  const [scoreA, scoreB] = matchSetScores(match);

  return (
    <div
      role={clickable ? 'button' : undefined}
      onClick={clickable ? onClick : undefined}
      className={`match ${isTBD ? 'is-tbd' : ''} ${fill ? 'is-fill' : ''}`}
      style={fill ? { width: '100%', height: '100%' } : undefined}
    >
      <div className="match-head">
        <span>{label ?? `Раунд ${match.round} · Матч ${match.slot}`}</span>
        {isFinal && (
          <span style={{ color: 'var(--accent-navy)', fontWeight: 600 }}>ФИНАЛ</span>
        )}
      </div>
      <Row
        name={match.playerAName}
        score={scoreA}
        winner={winnerIsA}
        loser={winnerIsB}
        place={match.playerAId ? placeByParticipant?.get(match.playerAId) : undefined}
      />
      <Row
        name={match.playerBName}
        score={scoreB}
        winner={winnerIsB}
        loser={winnerIsA}
        place={match.playerBId ? placeByParticipant?.get(match.playerBId) : undefined}
      />
    </div>
  );
}

/** Возвращает [счёт A, счёт B] по сетам, или [null, null] если матч не сыгран. */
function matchSetScores(match: Match): [number | null, number | null] {
  const score = match.score;
  if (!score) return [null, null];
  if (score.sets.length > 0) {
    const a = score.sets.filter((s) => s.a > s.b).length;
    const b = score.sets.filter((s) => s.b > s.a).length;
    return [a, b];
  }
  const m = score.summary.match(/^(\d+):(\d+)$/);
  if (!m) return [null, null];
  return [Number(m[1]), Number(m[2])];
}

function Row({
  name,
  score,
  winner,
  loser,
  place,
}: {
  name: string | null;
  score: number | null;
  winner: boolean;
  loser: boolean;
  place?: string | null;
}) {
  return (
    <div className={`match-row ${winner ? 'is-winner' : ''} ${loser ? 'is-loser' : ''}`}>
      <div className="match-name">
        {winner ? (
          <span className="check">
            <Check size={12} strokeWidth={3} />
          </span>
        ) : (
          <span style={{ width: 12, flexShrink: 0 }} />
        )}
        <span className="player-name">
          {name ?? <span style={{ color: 'var(--fg-faint)' }}>TBD</span>}
        </span>
        {place && <PlaceBadge place={place} size="sm" />}
      </div>
      <span className="match-score">{score != null ? score : ''}</span>
    </div>
  );
}
